import {
  Button,
  Text,
  makeStyles,
  mergeClasses,
} from "@fluentui/react-components";
import { ArrowLeftRegular } from "@fluentui/react-icons";
import { useNavigate } from "react-router-dom";
import {
  useSharedButtonStyles,
  useSharedPageContainerStyles,
  useSharedTextStyles,
} from "theme/sharedStyles";
import { designTokens } from "theme/customTheme";

const useStyles = makeStyles({
  pageContainer: {
    height: "100vh",
    overflowY: "auto",
  },
  contentContainer: {
    width: "100%",
    maxWidth: "960px",
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[6],
    minWidth: 0,
  },
  contentCard: {
    backgroundColor: designTokens.neutral.white,
    border: `1px solid ${designTokens.neutral[200]}`,
    borderRadius: designTokens.borderRadius.xl,
    boxShadow: designTokens.shadow.sm,
    padding: designTokens.spacing[8],
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[6],
    "@media (max-width: 768px)": {
      padding: designTokens.spacing[5],
    },
  },
  headingBlock: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[4],
    flexWrap: "wrap",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[3],
  },
  bodyText: {
    lineHeight: String(designTokens.lineHeight.relaxed),
  },
  list: {
    margin: 0,
    paddingLeft: designTokens.spacing[5],
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[2],
    color: designTokens.neutral[600],
  },
  listItem: {
    lineHeight: String(designTokens.lineHeight.relaxed),
    fontFamily: designTokens.fontFamily,
    fontSize: designTokens.fontSize.sm,
  },
  backButton: {
    fontWeight: designTokens.fontWeight.normal,
    borderRadius: designTokens.borderRadius.lg,
    paddingLeft: designTokens.spacing[3],
    paddingRight: designTokens.spacing[4],
    minHeight: designTokens.controlHeight.md,
    ":hover": {
      backgroundColor: designTokens.neutral[100],
      borderTopColor: designTokens.neutral[400],
      borderRightColor: designTokens.neutral[400],
      borderBottomColor: designTokens.neutral[400],
      borderLeftColor: designTokens.neutral[400],
    },
  },
});

export const GuidelinesPage = (): JSX.Element => {
  const styles = useStyles();
  const containerStyles = useSharedPageContainerStyles();
  const buttonStyles = useSharedButtonStyles();
  const textStyles = useSharedTextStyles();
  const navigate = useNavigate();

  return (
    <div
      className={mergeClasses(containerStyles.container, styles.pageContainer)}
    >
      <div className={styles.contentContainer}>
        <div className={styles.contentCard}>
          <div className={styles.headingBlock}>
            <Button
              className={styles.backButton}
              appearance="outline"
              icon={<ArrowLeftRegular />}
              onClick={() => navigate("/")}
            >
              Back to Home
            </Button>
            <h1 className={textStyles.heading1}>AI Guidelines</h1>
          </div>

          <section className={styles.section}>
            <h2 className={textStyles.heading3}>Summary</h2>
            <Text className={mergeClasses(textStyles.body, styles.bodyText)}>
              AIMS is NH&apos;s proprietary LLM tool that leverages Generative
              AI to help generate content with NH information and in the
              standard format, level of detail, and NH voice.
            </Text>
            <Text className={mergeClasses(textStyles.body, styles.bodyText)}>
              It can be utilized to draft the initial proposal responses,
              qualifications, summarizing and editing executive summaries. It
              can also be utilized to reformat resumes into the NH format, but
              the resume must be uploaded and verified by the individual that
              the resume is for. AIMS provides an easy way to query any of the
              NH Knowledge components that have been validated and considered
              gold standard. AIMS reduces the barrier to entry for utilizing AI
              in content creation and refinement.
            </Text>
          </section>

          <section className={styles.section}>
            <h2 className={textStyles.heading3}>Limitations/Known-Issues</h2>
            <ul className={styles.list}>
              <li className={styles.listItem}>
                AIMS only includes those proposals and quals that have been
                reviewed and updated to the gold standard. It is not all
                inclusive of every proposal, qual, project, or team.
              </li>
              <li className={styles.listItem}>
                AIMS should NOT be used to search for or recommend a list of
                team members to be included in the proposal or a list of team
                members with a certain skill, etc. Any query for a list of
                people should be redirected to Resource Management. This is NOT
                a tool for recommending people.
              </li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};
